import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Card, Row, Col, ListGroup, Table, Badge, Button } from "react-bootstrap";
import { FaLink, FaCheck } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getCurrentUser } from "../../common/utils";

// As páginas de versão ficam em /changelog (fonte única do changelog do produto) e
// são embutidas no bundle, para não depender de acesso ao repositório.
const arquivos = import.meta.glob("../../../changelog/v*.md", {
    query: "?raw",
    import: "default",
    eager: true,
});

const comparaVersao = (a, b) => {
    const partesA = a.versao.split(".").map(Number);
    const partesB = b.versao.split(".").map(Number);

    for (let i = 0; i < 3; i++) {
        if ((partesB[i] || 0) !== (partesA[i] || 0)) {
            return (partesB[i] || 0) - (partesA[i] || 0);
        }
    }

    return 0;
};

const versoes = Object.entries(arquivos)
    .map(([caminho, conteudo]) => {
        const versao = caminho.split("/").pop().replace(/^v/, "").replace(/\.md$/, "");
        const data = conteudo.match(/^#\s*v[\d.]+\s*-\s*\((.+?)\)/m)?.[1] || "";

        return { versao, data, conteudo };
    })
    .sort(comparaVersao);

const Changelog = () => {
    // versão selecionada fica na URL (?v=1.14.1) para poder copiar/compartilhar o link direto
    const [searchParams, setSearchParams] = useSearchParams();
    const [copiado, setCopiado] = useState(false);

    if (!getCurrentUser()?.is_superuser) {
        return <Navigate to="/sem-permissao" />;
    }

    const versaoParam = searchParams.get("v");
    const versaoSelecionada = versoes.some((v) => v.versao === versaoParam)
        ? versaoParam
        : versoes[0]?.versao || "";

    const versaoAtual = versoes.find((v) => v.versao === versaoSelecionada);

    const selecionarVersao = (versao) => {
        setSearchParams({ v: versao });
        setCopiado(false);
    };

    const copiarLink = () => {
        const url = `${window.location.origin}${window.location.pathname}?v=${versaoSelecionada}`;
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url).then(() => {
                setCopiado(true);
                setTimeout(() => setCopiado(false), 2000);
            });
        }
    };

    return (
        <div>
            <Row className="align-items-center">
                <Col sm={6} lg={6}>
                    <h4 className="page-title">Changelog</h4>
                </Col>
            </Row>

            <Row>
                <Col md={3} xs={12}>
                    <Card className="cardDestaque">
                        <Card.Body>
                            <h4>Versões</h4>
                            <ListGroup variant="flush">
                                {versoes.map((v, index) => (
                                    <ListGroup.Item
                                        action
                                        key={v.versao}
                                        active={v.versao === versaoSelecionada}
                                        onClick={() => selecionarVersao(v.versao)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <strong>v{v.versao}</strong>{" "}
                                        {index === 0 && <Badge bg="success">Atual</Badge>}
                                        <div>
                                            <small>{v.data}</small>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={9} xs={12}>
                    <Card className="cardDestaque">
                        <Card.Body>
                            {versaoAtual ? (
                                <>
                                    <div className="d-flex justify-content-end mb-2">
                                        <Button
                                            variant={copiado ? "success" : "outline-secondary"}
                                            size="sm"
                                            onClick={copiarLink}
                                            title="Copiar o link direto desta versão"
                                        >
                                            {copiado ? (
                                                <><FaCheck className="me-1" /> Link copiado!</>
                                            ) : (
                                                <><FaLink className="me-1" /> Copiar link desta versão</>
                                            )}
                                        </Button>
                                    </div>
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{
                                            table: (props) => (
                                                <Table responsive className="custom-table" {...props} />
                                            ),
                                        }}
                                    >
                                        {versaoAtual.conteudo}
                                    </ReactMarkdown>
                                </>
                            ) : (
                                <p>Nenhuma versão encontrada.</p>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Changelog;
